"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Asp, DetailBody, Modal } from "./BattleDemoParts";
import { ActionOption, BASE_LABEL, BattleData, BattleView, Plan, RunSummary, UnitSnap, UnitView } from "./battleDemoTypes";

type Tab = "menu" | "act" | "item";

/** 戦闘ログの 1 行。数字を目立たせる */
function logText(line: string): React.ReactNode[] {
  return line.split(/(\d+)/).map((part, i) => (i % 2 === 1 ? <b key={i}>{part}</b> : part));
}

/** 行動の届く範囲の表記 */
function rangeLabel(a: ActionOption): string {
  const t = a.target;
  if (t.scope === "self" || t.side === "self") return "自分";
  const who = t.side === "enemy" ? "敵" : t.side === "ally" ? "味方" : "誰でも";
  const down = t.state === "down" ? "倒れた" : "";
  if (t.scope === "single") return `${down}${who} 1 体`;
  if (t.scope === "all") return `${down}${who}の全体`;
  if (t.scope === "others") return `自分以外の${who}`;
  if (t.scope === "random") return `${who}から無作為に ${t.count ?? 1} 体`;
  if (t.scope === "group") return `同じ種の${who}`;
  return who;
}
type ModalState = { type: "detail"; id: string } | { type: "log" } | { type: "menu" } | { type: "result" } | null;
type Fx = { n: number; text: string; kind: "dmg" | "heal" };

/** モディファイアのアイコンに出す名。アイコンの名（short）か表記名の、頭の 4 文字 */
const CHIP_LABEL_MAX = 4;
function chipLabel(label: string): string {
  return [...label].slice(0, CHIP_LABEL_MAX).join("");
}

const SPEED_KEY = "rakuen-battle-demo-speed";
const LINE_MS = 520;
const TURN_MS = 380;

function readSpeed(): number {
  try {
    const v = Number(window.localStorage.getItem(SPEED_KEY));
    return v === 2 || v === 3 ? v : 1;
  } catch {
    return 1;
  }
}

/** 戦闘の画面。上から 見出し・敵のパネル・戦闘ログ（5 行）・味方のパネル・コマンドの欄 を縦に積む。パネルは正方形を横に 4 つまで並べる */
export default function BattleDemoBattle({
  data, busy, last, over, onSubmit, onNext, onTop, onAbandon, mode = "demo",
}: {
  data: BattleData;
  busy: boolean;
  last: RunSummary["last"];
  over: boolean;
  onSubmit: (plans: Record<string, Plan>) => void;
  onNext: () => void;
  onTop: () => void;
  onAbandon: () => void;
  /** demo は戦闘デモプレイ、play は進行の画面の一時画面。文言と見出しが変わる */
  mode?: "demo" | "play";
}) {
  const play = mode === "play";
  const [view, setView] = useState<BattleView>(data.view);
  const [snap, setSnap] = useState<Record<string, UnitSnap> | null>(null);
  const [lines, setLines] = useState<string[]>([]);
  const [playing, setPlaying] = useState(false);
  const [fx, setFx] = useState<Record<string, Fx>>({});
  const [speed, setSpeed] = useState(1);
  const [plans, setPlans] = useState<Record<string, Plan>>({});
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [tab, setTab] = useState<Tab>("menu");
  const [picking, setPicking] = useState<ActionOption | null>(null);
  const [modal, setModal] = useState<ModalState>(null);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fast = useRef(false);
  const speedRef = useRef(1);
  const shownId = useRef<string>("");
  const lastHp = useRef<Record<string, number>>({});
  const fxCount = useRef(0);
  const doneLines = useRef<string[]>([]);

  useEffect(() => {
    const s = readSpeed();
    setSpeed(s);
    speedRef.current = s;
  }, []);

  const cycleSpeed = () => {
    const next = speed >= 3 ? 1 : speed + 1;
    setSpeed(next);
    speedRef.current = next;
    try { window.localStorage.setItem(SPEED_KEY, String(next)); } catch { /* 保存できなくても動く */ }
  };

  // 応答が届くたびに、戦闘ログを 1 行ずつ出す
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const fresh = shownId.current !== data.view.id;
    if (fresh) {
      shownId.current = data.view.id;
      setView(data.view);
      setSnap(null);
      setModal(null);
      doneLines.current = [];
      lastHp.current = {};
      for (const u of data.view.allies.concat(data.view.enemies)) lastHp.current[u.id] = u.hp.current;
    }
    setPlans({});
    setLabels({});
    setPicking(null);
    setTab("menu");
    setLines(doneLines.current);
    const base = doneLines.current;

    const finish = (all: string[]) => {
      doneLines.current = all;
      setLines(all);
      setView(data.view);
      setSnap(null);
      setPlaying(false);
      lastHp.current = {};
      for (const u of data.view.allies.concat(data.view.enemies)) lastHp.current[u.id] = u.hp.current;
      if (data.view.result) setModal({ type: "result" });
    };

    // 途中から開き直した戦闘は、ここまでのログをまとめて出す
    if (data.log_from === 0 && data.view.turn > 1) {
      finish(data.log);
      return;
    }

    const events = data.events;
    const all = base.concat(events.map((e) => e.text));
    let i = 0;
    fast.current = false;
    setPlaying(true);
    const step = () => {
      if (i >= events.length || fast.current) {
        finish(all);
        return;
      }
      const ev = events[i++];
      setLines((prev) => prev.concat(ev.text));
      if (ev.snap) {
        const next: Record<string, Fx> = {};
        for (const [id, s] of Object.entries(ev.snap)) {
          const before = lastHp.current[id];
          if (before !== undefined && s.hp !== before) {
            fxCount.current += 1;
            next[id] = s.hp < before ? { n: fxCount.current, text: String(before - s.hp), kind: "dmg" } : { n: fxCount.current, text: `+${s.hp - before}`, kind: "heal" };
          }
          lastHp.current[id] = s.hp;
        }
        setSnap(ev.snap);
        if (Object.keys(next).length) setFx((prev) => ({ ...prev, ...next }));
      }
      const wait = ev.text.startsWith("## ") ? TURN_MS : Math.round(LINE_MS / speedRef.current);
      timer.current = setTimeout(step, wait);
    };
    step();
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [data]);

  const names = useMemo(() => {
    const m: Record<string, string> = {};
    for (const u of data.view.allies.concat(data.view.enemies, view.allies, view.enemies)) m[u.id] = u.name;
    return m;
  }, [data.view, view]);

  const awaiting = view.awaiting;
  const finished = view.result !== null;
  const current = playing || finished ? null : awaiting.find((id) => !plans[id]) ?? null;
  const opts = current ? data.options[current] : null;
  const ready = !playing && !finished && awaiting.length > 0 && awaiting.every((id) => plans[id]);

  const setPlan = (id: string, plan: Plan, label: string) => {
    setPlans((prev) => ({ ...prev, [id]: plan }));
    setLabels((prev) => ({ ...prev, [id]: label }));
    setPicking(null);
    setTab("menu");
  };

  /** 逃亡。押した者を逃亡にし、まだ決めていない味方を防御で埋めて、行動の確認へ進む */
  const FLEE_LABEL = "逃亡";
  const FLEE_BLOCKED_LINE = "逃げられない。";
  const flee = (id: string) => {
    // 逃亡を封じる状態の味方が居るときは、逃げられないとログの欄に出して選ばせない
    if (data.options[id]?.flee_blocked) {
      setLines((prev) => prev.concat(FLEE_BLOCKED_LINE));
      return;
    }
    const rest = awaiting.filter((x) => x !== id && !plans[x]);
    setPlans((prev) => ({ ...prev, [id]: { kind: "flee", targets: [] }, ...Object.fromEntries(rest.map((x) => [x, { kind: "guard", targets: [] } as Plan])) }));
    setLabels((prev) => ({ ...prev, ...Object.fromEntries([id, ...rest].map((x) => [x, FLEE_LABEL])) }));
    setPicking(null);
    setTab("menu");
  };

  const commit = useCallback((a: ActionOption, targets: string[]) => {
    if (!current) return;
    const plan: Plan = { kind: a.kind, targets };
    if (a.kind === "skill") plan.skill = a.key;
    if (a.weapon) plan.weapon = a.weapon;
    if (a.kind === "item" && a.item) plan.item = a.item;
    if (a.via) plan.via = a.via;
    const to = targets.length ? ` → ${targets.map((t) => names[t]).join("・")}` : "";
    const by = a.via && a.user ? `${a.user}の` : "";
    setPlan(current, plan, `${by}${a.kind === "attack" ? "攻撃" : a.name}${to}`);
  }, [current, names]);

  const choose = (a: ActionOption) => {
    if (!a.selectable) return;
    if (a.target.pick) setPicking(a);
    else commit(a, []);
  };

  const undo = () => {
    const done = awaiting.filter((id) => plans[id]);
    const lastId = done[done.length - 1];
    if (!lastId) return;
    // 逃亡を選んだ後は、逃亡の者と、逃亡で埋めた味方をまとめて戻す
    const fled = done.some((id) => plans[id].kind === "flee");
    const back = fled ? done.filter((id) => labels[id] === FLEE_LABEL) : [lastId];
    setPlans((prev) => { const n = { ...prev }; for (const id of back) delete n[id]; return n; });
    setLabels((prev) => { const n = { ...prev }; for (const id of back) delete n[id]; return n; });
    setPicking(null);
  };

  const tapUnit = (u: UnitView) => {
    if (picking && picking.target.candidates.includes(u.id)) {
      commit(picking, [u.id]);
      return;
    }
    if (picking) return;
    setModal({ type: "detail", id: u.id });
  };

  const renderUnit = (u: UnitView) => {
    const s = snap ? snap[u.id] : undefined;
    const hp = s ? s.hp : u.hp.current;
    const max = s ? s.max : u.hp.max;
    const inSlot = s ? s.in : true;
    const alive = inSlot && hp > 0;
    const ratio = max > 0 ? hp / max : 0;
    const shield = s ? s.shield : u.shield;
    const f = fx[u.id];
    const cls = ["bd-unit"];
    if (!alive) cls.push("bd-unit-down");
    if (u.id === current) cls.push("bd-unit-actor");
    if (picking) cls.push(picking.target.candidates.includes(u.id) ? "bd-unit-pickable" : "bd-unit-unpick");
    const poolNow = (i: number, fallback: number) => (s && s.pools[i] !== undefined ? s.pools[i] : fallback);
    const ally = u.side === "ally";
    return (
      <button key={u.id} className={cls.join(" ")} onClick={() => tapUnit(u)}>
        <div key={f ? `s${f.n}` : "s"} className={`bd-u-body ${f && f.kind === "dmg" ? "bd-unit-hit" : ""}`}>
          <div className="bd-u-name">{u.name}</div>
          <div className={`bd-bar ${ratio < 0.3 ? "bd-bar-low" : ratio < 0.6 ? "bd-bar-mid" : ""}`}>
            <i style={{ width: `${Math.max(0, Math.min(1, ratio)) * 100}%` }} />
            {shield > 0 && <b style={{ width: `${Math.min(100, (shield / Math.max(1, max)) * 100)}%` }} />}
            {ally && <span>{hp}/{max}</span>}
          </div>
          {u.pools.map((p, i) => (
            <div key={p.type} className="bd-bar bd-bar-mp">
              <i style={{ width: `${p.max > 0 ? (poolNow(i, p.current) / p.max) * 100 : 0}%` }} />
              {ally && <span>{poolNow(i, p.current)}/{p.max}</span>}
            </div>
          ))}
          <div className="bd-u-mods">
            {rideOf(u) && <span className="bd-chip bd-chip-ride" title={`${names[rideOf(u) as string] ?? ""}に搭乗`}>搭乗</span>}
            {(s ? s.guarding : u.guarding) && <span className="bd-chip">防御</span>}
            {u.modifiers.map((m) => (
              <span key={m.name} className={`bd-chip bd-chip-${m.kind}`} title={m.label}>{chipLabel(m.short || m.label)}{m.stacks > 1 ? `×${m.stacks}` : ""}{typeof m.turns_left === "number" ? m.turns_left : ""}</span>
            ))}
          </div>
        </div>
        {f && <span key={f.n} className={`bd-float bd-float-${f.kind}`}>{f.text}</span>}
      </button>
    );
  };

  /** 乗っている乗せる者の id。ログを出している間は、その行の時点の値 */
  const rideOf = (u: UnitView): string | null => {
    const s = snap ? snap[u.id] : undefined;
    return s && s.carrier !== undefined ? s.carrier ?? null : u.carrier ?? null;
  };

  /** 枠を取らない乗せる者（乗っている者が居る乗り物・誰も乗っていない乗り物）。枠の並びの上に帯で出す */
  const renderBand = (u: UnitView) => {
    const s = snap ? snap[u.id] : undefined;
    const hp = s ? s.hp : u.hp.current;
    const max = s ? s.max : u.hp.max;
    const alive = (s ? s.in : true) && hp > 0;
    const ratio = max > 0 ? hp / max : 0;
    const shield = s ? s.shield : u.shield;
    const f = fx[u.id];
    const riders = (u.riders ?? []).map((id) => names[id]).filter(Boolean);
    const cls = ["bd-band"];
    if (!alive) cls.push("bd-unit-down");
    if (u.parked) cls.push("bd-band-parked");
    if (picking) cls.push(picking.target.candidates.includes(u.id) ? "bd-unit-pickable" : "bd-unit-unpick");
    return (
      <button key={u.id} className={cls.join(" ")} onClick={() => tapUnit(u)}>
        <div className="bd-u-name">{u.name}</div>
        <div className={`bd-bar ${ratio < 0.3 ? "bd-bar-low" : ratio < 0.6 ? "bd-bar-mid" : ""}`}>
          <i style={{ width: `${Math.max(0, Math.min(1, ratio)) * 100}%` }} />
          {shield > 0 && <b style={{ width: `${Math.min(100, (shield / Math.max(1, max)) * 100)}%` }} />}
          {u.side === "ally" && <span>{hp}/{max}</span>}
        </div>
        <span className="bd-band-riders">{riders.length ? riders.join("・") : "空き"}</span>
        {u.modifiers.length > 0 && (
          <div className="bd-u-mods">
            {u.modifiers.map((m) => (
              <span key={m.name} className={`bd-chip bd-chip-${m.kind}`} title={m.label}>{chipLabel(m.short || m.label)}{m.stacks > 1 ? `×${m.stacks}` : ""}{typeof m.turns_left === "number" ? m.turns_left : ""}</span>
            ))}
          </div>
        )}
        {f && <span key={f.n} className={`bd-float bd-float-${f.kind}`}>{f.text}</span>}
      </button>
    );
  };

  const row = (units: UnitView[], side: "enemy" | "ally") => {
    const isBand = (u: UnitView) => !!u.carry && ((u.riders ?? []).length > 0 || !!u.parked);
    const bands = units.filter(isBand);
    const slots = units.filter((u) => !isBand(u));
    const cells: React.ReactNode[] = slots.map(renderUnit);
    for (let i = slots.length; i < 4; i++) cells.push(<div key={`e${i}`} className="bd-unit-gap" />);
    const grid = <div className={`bd-unit-row bd-unit-row-${side}`}>{cells}</div>;
    if (bands.length === 0) return grid;
    // 敵の帯は枠の下（ログの側）に、味方の帯は枠の上（ログの側）に出す
    return (
      <div className={`bd-side bd-side-${side}`}>
        {side === "ally" && bands.map(renderBand)}
        {grid}
        {side === "enemy" && bands.map(renderBand)}
      </div>
    );
  };

  const actions = opts ? opts.actions.filter((a) => (tab === "item" ? a.kind === "item" : a.kind !== "item")) : [];
  const attackCount = opts ? opts.actions.filter((x) => x.kind === "attack").length : 0;
  const resultLabel = view.result === "victory" ? "勝利" : view.result === "defeat" ? "敗北" : "逃亡";

  return (
    <div className="bd-battle">
      <header className="bd-battle-head">
        <div className="bd-bh-left">
          {!play && <span className="bd-stage">{data.stage} 回目</span>}
          {data.boss && <span className="bd-boss">BOSS</span>}
          <span className="bd-turn">ターン {view.turn}</span>
        </div>
        <div className="bd-bh-right">
          <button className="bd-icon-btn" onClick={cycleSpeed} title="速さ">×{speed}</button>
          <button className="bd-icon-btn" onClick={() => setModal({ type: "log" })}>ログ</button>
          <button className="bd-icon-btn" onClick={() => setModal({ type: "menu" })}>≡</button>
        </div>
      </header>

      {row(view.enemies, "enemy")}

      <div className="bd-log-wrap">
      <div className="bd-log">
        <div className="bd-log-lines">
            {lines.slice(-40).map((line, i, shown) => {
              const age = shown.length - 1 - i;
              const kind = line.startsWith("## ") ? "bd-log-turn" : /^(敵を全て倒した|味方は全滅した|逃げ出した)/.test(line) ? "bd-log-result" : "";
              return <div key={lines.length - shown.length + i} className={`bd-log-line bd-log-age${age} ${kind}`}>{line.startsWith("## ") ? line.slice(3) : logText(line)}</div>;
            })}
          </div>
      </div>
        {playing && <button className="bd-skip" onClick={() => { fast.current = true; }}>早送り ▸▸</button>}
      </div>

      {row(view.allies, "ally")}

      <div className="bd-command">
        {playing && <div className="bd-wait">戦いが進んでいる…</div>}

        {!playing && finished && (
          <div className="bd-wait"><button className="bd-btn bd-btn-primary" onClick={() => setModal({ type: "result" })}>結果を見る</button></div>
        )}

        {!playing && !finished && awaiting.length === 0 && (
          <div className="bd-menu bd-menu-one">
            <button className="bd-menu-btn bd-menu-go" disabled={busy} onClick={() => onSubmit({})}>進める<small>このターンは誰も行動を選べない</small></button>
          </div>
        )}

        {ready && (
          <>
            <div className="bd-plans">
              {awaiting.map((id) => <div key={id}><b>{names[id]}</b><span>{labels[id]}</span></div>)}
            </div>
            <div className="bd-decide">
              <button className="bd-btn" disabled={busy} onClick={undo}>戻る</button>
              <button className="bd-btn bd-btn-primary" disabled={busy} onClick={() => onSubmit(plans)}>決定</button>
            </div>
          </>
        )}

        {current && opts && !picking && tab === "menu" && (
          <>
            <div className="bd-menu">
              <button className="bd-menu-btn" onClick={() => setTab("act")}>行動</button>
              <button className="bd-menu-btn" onClick={() => setPlan(current, { kind: "guard", targets: [] }, "防御")}>防御</button>
              <button className="bd-menu-btn" onClick={() => setTab("item")}>道具</button>
              {opts.fourth === "flee" && (
                <button className="bd-menu-btn" onClick={() => flee(current)}>逃亡</button>
              )}
              {(opts.fourth === "return" || opts.fourth === "vanish") && (
                <button className="bd-menu-btn" onClick={() => setPlan(current, { kind: "dismiss", targets: [] }, opts.fourth === "return" ? "戻す" : "消滅")}>
                  {opts.fourth === "return" ? "戻す" : "消滅"}
                </button>
              )}
              {!opts.fourth && <div />}
            </div>
            {awaiting.some((id) => plans[id]) && <button className="bd-undo" onClick={undo}>◀ 前のキャラを選び直す</button>}
          </>
        )}

        {current && opts && !picking && tab !== "menu" && (
          <div className="bd-cmd-list">
            <button className="bd-opt bd-opt-back" onClick={() => setTab("menu")}><span className="bd-on">◀ 戻る</span></button>
            {actions.length === 0 && <div className="bd-cmd-note">{tab === "item" ? "使える道具が無い。" : "選べる行動が無い。"}</div>}
            {actions.map((a) => (
              <button key={a.key} className={`bd-opt ${a.selectable ? "" : "bd-opt-disabled"}`} onClick={() => choose(a)}>
                <span className="bd-o1">
                  <span className="bd-on">{a.kind === "attack" ? (attackCount > 1 ? `攻撃（${a.name}）` : "攻撃") : a.name}{a.count !== null ? ` ×${a.count}` : ""}</span>
                  <span className="bd-asps">{a.aspects.map((x) => <Asp key={x} label={x} />)}</span>
                </span>
                <span className="bd-o2">
                  {a.via && a.user && <span className="bd-o-user">{a.user}</span>}
                  <span>{rangeLabel(a)}</span>
                  {a.cooldown_left > 0 ? <span className="bd-cd-wait">あと {a.cooldown_left} ターン</span> : a.cooldown > 0 ? <span>待ち {a.cooldown}</span> : null}
                </span>
              </button>
            ))}
          </div>
        )}

        {current && picking && (
          <div className="bd-cmd-list">
            <button className="bd-opt bd-opt-back" onClick={() => setPicking(null)}><span className="bd-on">◀ 戻る</span><span className="bd-o2"><span>{picking.name} の対象</span></span></button>
            {picking.target.candidates.map((id) => {
              const u = view.allies.concat(view.enemies).find((x) => x.id === id);
              return (
                <button key={id} className="bd-opt" onClick={() => commit(picking, [id])}>
                  <span className="bd-o1"><span className="bd-on">{names[id]}</span></span>
                  <span className="bd-o2"><span>{u && u.side === "ally" ? `HP ${u.hp.current}/${u.hp.max}` : "敵"}</span></span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {modal && modal.type === "detail" && data.details[modal.id] && (
        <Modal onClose={() => setModal(null)}><DetailBody detail={data.details[modal.id]} /></Modal>
      )}

      {modal && modal.type === "log" && (
        <Modal onClose={() => setModal(null)}>
          <div className="bd-m-title">戦闘ログ</div>
          <pre className="bd-pre">{lines.map((l) => (l.startsWith("## ") ? `\n${l.slice(3)}` : `・${l}`)).join("\n").trim()}</pre>
        </Modal>
      )}

      {modal && modal.type === "menu" && (
        <Modal onClose={() => setModal(null)}>
          <div className="bd-m-title">メニュー</div>
          <div className="bd-m-sec">
            <h3>詳細を見る</h3>
            <div className="bd-m-list">
              {view.allies.concat(view.enemies).map((u) => (
                <button key={u.id} className="bd-opt" onClick={() => setModal({ type: "detail", id: u.id })}>
                  <div className="bd-o1"><span className="bd-on">{u.name}</span><span className="bd-oc">{u.side === "ally" ? `味方・HP ${u.hp.current}/${u.hp.max}` : "敵"}</span></div>
                </button>
              ))}
            </div>
          </div>
          <div className="bd-btn-row"><button className={play ? "bd-btn" : "bd-btn bd-btn-danger"} onClick={() => { setModal(null); onAbandon(); }}>{play ? "物語の画面へ戻る" : "デモを終える"}</button></div>
        </Modal>
      )}

      {modal && modal.type === "result" && finished && (
        <Modal onClose={() => setModal(null)} locked>
          <div className={`bd-result-big bd-result-${view.result}`}>{resultLabel}</div>
          <div className="bd-result-sub">{play ? `${view.turn} ターン` : `${data.stage} 回目・${view.turn} ターン`}</div>
          {view.result === "victory" && last && last.reward && (
            <>
              <div className="bd-kv">
                <div><span>報酬</span><b>{last.reward.total} CC</b></div>
                <div><span>1 人あたり</span><b>{last.reward.share} CC</b></div>
              </div>
              <div className="bd-m-sec">
                <h3>成長</h3>
                <div className="bd-m-list">
                  {view.allies.filter((u) => !u.source).map((u) => {
                    const g = (last.growth ?? {})[u.id] ?? {};
                    const text = Object.entries(g).map(([k, v]) => `${BASE_LABEL[k] ?? k}+${v}`).join("　");
                    return <div key={u.id} className="bd-m-item"><div className="bd-mi1"><b>{u.name}</b><span>{text || "—"}</span></div></div>;
                  })}
                </div>
              </div>
            </>
          )}
          {view.result !== "victory" && !play && <div className="bd-result-sub">デモはここで終わり。次は最初から始まる。</div>}
          <div className="bd-btn-row">
            <button className="bd-btn" onClick={() => setModal({ type: "log" })}>ログ</button>
            {play
              ? <button className="bd-btn bd-btn-primary" disabled={busy} onClick={onTop}>物語へ戻る</button>
              : view.result === "victory" && !over
                ? <button className="bd-btn bd-btn-primary" disabled={busy} onClick={onNext}>{last && last.shop ? "購入・修理フェーズへ" : "次の戦闘へ"}</button>
                : <button className="bd-btn bd-btn-primary" disabled={busy} onClick={onTop}>最初へ</button>}
          </div>
        </Modal>
      )}
    </div>
  );
}
